import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SectionHeader from './ui/SectionHeader';
import Modal from './ui/Modal';

const Certification = () => {
  const [selectedCert, setSelectedCert] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const certifications = [
    {
      id: 'P3K',
      title: 'First Aider (Petugas P3K)',
      issuer: 'Kementerian Ketenagakerjaan Republik Indonesia',
      date: 'May 2026',
      status: 'May 2026',
    },
    {
      id: 'NEB',
      title: 'NEBOSH IGC',
      issuer: 'NEBOSH',
      date: 'Not issued',
      status: 'Not issued',
      note: 'Certificate not yet issued.',
    },
    {
      id: 'AGT',
      title: 'Authorized Gas Tester (AGT)',
      issuer: 'BNSP, LSP PPSDM Migas',
      date: 'Apr 2026',
      status: 'Apr 2026',
    },
    {
      id: 'K3M',
      title: 'Operator K3 Migas',
      issuer: 'BNSP, LSP PPSDM Migas Cepu',
      credentialId: '060013257300042692023',
      date: 'Dec 2023',
      image: '/images/operator migas cepu.jpg',
      status: 'Issued',
    },
    {
      id: 'ISO',
      title: 'ISO 9001, ISO 14001, ISO 45001 and SMK3',
      issuer: 'LSP Training Consulting',
      date: 'Jan 2022',
      image: '/images/ISO LSP.jpg',
      status: 'Awareness',
      note: 'Awareness course on requirements and documentation, 20-21 January 2022.',
    },
    {
      id: 'TOT',
      title: 'Instruktur Pelaksana Pelatihan Tatap Muka',
      issuer: 'Badan Nasional Sertifikasi Profesi (BNSP)',
      credentialId: '8091024404000102812019',
      date: 'Sep 2019',
      image: '/images/TOT bnsp.jpg',
      status: 'Issued',
    },
    {
      id: 'AK3',
      title: 'Occupational Safety and Health Expert',
      issuer: 'Kementerian Ketenagakerjaan Republik Indonesia',
      credentialId: '6326/AK3/U/X/2016',
      date: 'Aug 2016',
      image: '/images/k3 umum kemnaker.jpg',
      status: 'Issued',
    },
  ];

  const openCert = (cert, index) => {
    setSelectedCert(cert);
    setCurrentIndex(index);
  };

  const handleNext = (event) => {
    event.stopPropagation();
    const nextIndex = (currentIndex + 1) % certifications.length;
    setCurrentIndex(nextIndex);
    setSelectedCert(certifications[nextIndex]);
  };

  const handlePrevious = (event) => {
    event.stopPropagation();
    const prevIndex = (currentIndex - 1 + certifications.length) % certifications.length;
    setCurrentIndex(prevIndex);
    setSelectedCert(certifications[prevIndex]);
  };

  return (
    <>
      <section id="certification" className="section-rule bg-btc-void">
        <div className="container-max section-padding py-24">
          <SectionHeader
            number="05"
            label="Credentials"
            title="Certificates"
            kicker="Issued credentials, an awareness course, and a NEBOSH IGC certificate that is not yet issued."
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {certifications.map((cert, index) => (
              <button
                key={cert.id}
                type="button"
                onClick={() => openCert(cert, index)}
                className="lift-card min-h-11 p-8 text-left"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="label">
                    {String(index + 1).padStart(2, '0')}. {cert.id}
                  </span>
                  <span className="rounded-full border border-btc-gold/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-btc-gold">
                    {cert.status}
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold leading-tight text-white md:text-2xl">
                  {cert.title}
                </h3>
                <p className="mt-4 text-sm text-btc-muted">{cert.issuer}</p>
                <p className="mt-2 font-mono text-xs text-btc-muted">{cert.date}</p>
              </button>
            ))}
          </div>

          <div className="surface-card mt-6 p-6 md:p-8">
            <p className="label mb-4">Credential IDs</p>
            <div className="space-y-3">
              {certifications
                .filter((cert) => cert.credentialId)
                .map((cert) => (
                  <div key={cert.id} className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
                    <span className="w-12 font-mono text-xs uppercase tracking-widest text-btc-orange">{cert.id}</span>
                    <span className="font-mono text-sm text-white">{cert.credentialId}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={Boolean(selectedCert)}
        onClose={() => setSelectedCert(null)}
        title={selectedCert?.title}
        indexLabel={`${String(currentIndex + 1).padStart(2, '0')} / ${String(certifications.length).padStart(2, '0')}`}
        actions={
          <>
            <button
              type="button"
              onClick={handlePrevious}
              aria-label="Previous certificate"
              className="icon-btn"
            >
              <ChevronLeft size={18} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={handleNext}
              aria-label="Next certificate"
              className="icon-btn"
            >
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </>
        }
      >
        {selectedCert && (
          <>
            {selectedCert.image ? (
              <div className="overflow-hidden rounded-xl border border-white/10 bg-btc-void">
                <img
                  src={selectedCert.image}
                  alt={selectedCert.title}
                  className="max-h-96 w-full object-contain"
                />
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 px-6 py-16 text-center font-mono text-xs uppercase tracking-widest text-btc-muted">
                Certificate image not yet available
              </div>
            )}
            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="label">Issuer</dt>
                <dd className="mt-1 text-white">{selectedCert.issuer}</dd>
              </div>
              <div>
                <dt className="label">Issued</dt>
                <dd className="mt-1 font-mono text-sm text-white">{selectedCert.date}</dd>
              </div>
              {selectedCert.credentialId && (
                <div className="sm:col-span-2">
                  <dt className="label">Credential ID</dt>
                  <dd className="mt-1 font-mono text-sm text-white">{selectedCert.credentialId}</dd>
                </div>
              )}
              {selectedCert.note && (
                <div className="sm:col-span-2">
                  <dt className="label">Note</dt>
                  <dd className="mt-1 text-btc-muted">{selectedCert.note}</dd>
                </div>
              )}
              <div>
                <dt className="label">Status</dt>
                <dd className="mt-1 font-mono text-sm text-btc-gold">{selectedCert.status}</dd>
              </div>
            </dl>
          </>
        )}
      </Modal>
    </>
  );
};

export default Certification;
